/*
Hook for handling bulk image downloads with progress tracking
Autor: Assistant
Fecha: 2025-01-28
*/
import { useState, useCallback } from 'react'

import useImageFetcher from './useImageFetcher'
import usePictureDelete from './useImageDelete'

interface DownloadProgress {
  current: number
  total: number
  isDownloading: boolean
  currentImage?: string
}

interface DeleteProgress {
  current: number
  total: number
  isDeleting: boolean
  currentImage?: string
  errors: string[]
  successfulDeletions: string[]
}

const useImageBulkDownload = () => {
  const { fetchImage, errorImage } = useImageFetcher()
  const { pictureDelete, errorPicture } = usePictureDelete()

  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress>({
    current: 0,
    total: 0,
    isDownloading: false,
  })

  const [deleteProgress, setDeleteProgress] = useState<DeleteProgress>({
    current: 0,
    total: 0,
    isDeleting: false,
    errors: [],
    successfulDeletions: [],
  })

  const downloadMultipleImages = useCallback(
    async (imageUrls: string[]) => {
      if (imageUrls.length === 0) return

      setDownloadProgress({
        current: 0,
        total: imageUrls.length,
        isDownloading: true,
      })

      try {
        for (let i = 0; i < imageUrls.length; i++) {
          const url = imageUrls[i]

          // Update progress with current image
          setDownloadProgress((prev) => ({
            ...prev,
            current: i + 1,
            currentImage: url,
          }))

          // Download the image
          await fetchImage(url)

          // Add small delay to prevent browser blocking multiple downloads
          // and to give user visual feedback
          if (i < imageUrls.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 800))
          }
        }

        // Download completed successfully
        setDownloadProgress({
          current: imageUrls.length,
          total: imageUrls.length,
          isDownloading: false,
        })
      } catch (error) {
        console.error('Error during bulk download:', error)
        setDownloadProgress((prev) => ({
          ...prev,
          isDownloading: false,
        }))
      }
    },
    [fetchImage]
  )

  const deleteMultipleImages = useCallback(
    async (
      imageUrls: string[],
      vehicleId: string,
      registry: boolean = false,
      onImageDeleted?: (deletedImageUrl: string) => void
    ) => {
      if (imageUrls.length === 0) return

      setDeleteProgress({
        current: 0,
        total: imageUrls.length,
        isDeleting: true,
        errors: [],
        successfulDeletions: [],
      })

      const errors: string[] = []
      const successfulDeletions: string[] = []

      try {
        for (let i = 0; i < imageUrls.length; i++) {
          const imageUrl = imageUrls[i]

          // Update progress with current image
          setDeleteProgress((prev) => ({
            ...prev,
            current: i + 1,
            currentImage: imageUrl,
          }))

          try {
            // Delete the image using existing delete hook
            await pictureDelete(imageUrl, vehicleId, registry, '')

            // Track successful deletion
            successfulDeletions.push(imageUrl)

            // Callback to update UI immediately
            if (onImageDeleted) {
              onImageDeleted(imageUrl)
            }
          } catch (error) {
            // Track error but continue with other deletions
            const errorMessage = error instanceof Error ? error.message : 'Error desconocido'
            errors.push(`${imageUrl}: ${errorMessage}`)
            console.error(`Error deleting image ${imageUrl}:`, error)
          }

          // Add small delay to prevent overwhelming the server
          if (i < imageUrls.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 500))
          }
        }

        // Final progress update
        setDeleteProgress((prev) => ({
          ...prev,
          current: imageUrls.length,
          isDeleting: false,
          errors,
          successfulDeletions,
        }))

        return {
          success: successfulDeletions.length,
          errors: errors.length,
          deletedImages: successfulDeletions,
        }
      } catch (error) {
        console.error('Error during bulk delete:', error)
        setDeleteProgress((prev) => ({
          ...prev,
          isDeleting: false,
          errors: [...prev.errors, 'Error general durante la eliminación'],
        }))

        return {
          success: successfulDeletions.length,
          errors: errors.length + 1,
          deletedImages: successfulDeletions,
        }
      }
    },
    [pictureDelete]
  )

  const resetProgress = useCallback(() => {
    setDownloadProgress({
      current: 0,
      total: 0,
      isDownloading: false,
    })
    setDeleteProgress({
      current: 0,
      total: 0,
      isDeleting: false,
      errors: [],
      successfulDeletions: [],
    })
  }, [])

  return {
    downloadMultipleImages,
    downloadProgress,
    deleteMultipleImages,
    deleteProgress,
    resetProgress,
    error: errorImage || errorPicture,
  }
}

export default useImageBulkDownload
